/**
 * Browser storage for the docs playground: keys in localStorage (they outlive the tab), the
 * customer session and remembered values in sessionStorage (they do not). Every write notifies
 * the hooks in this tab; the `storage` event covers other tabs. Storage can be missing or throw
 * (private mode, blocked site data), so reads fall back to null and writes are best effort.
 */

export type StorageArea = 'local' | 'session';

export const DOCS_STORE_KEY = 'nucleus:docs:store';
export const DOCS_SESSION_KEY = 'nucleus:docs:session';
export const docsKeysKey = (storeId: string) => `nucleus:docs:keys:${storeId}`;
export const docsSelectedKeyKey = (storeId: string) => `nucleus:docs:selected:${storeId}`;

const listeners = new Set<() => void>();

function storage(area: StorageArea): Storage | null {
  try {
    return area === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function readItem(area: StorageArea, key: string): string | null {
  try {
    return storage(area)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeItem(area: StorageArea, key: string, value: string | null): void {
  try {
    const target = storage(area);
    if (value === null) target?.removeItem(key);
    else target?.setItem(key, value);
  } catch {
    // Quota or blocked storage: the playground cannot remember anything; nothing to recover.
  }
  listeners.forEach((listener) => listener());
}

export function subscribeStorage(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

export function parseJson(raw: string | null): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}
